import React, { useMemo, useEffect, useState } from "react";
import { itemsService } from "../services/items.service";
import { GetItemTransactionValue, SetItemTransaction } from "../store/itemAtom";
import { formatCustomNumber } from "../helpers/formatCustomNumber";
import { formatCustomDate } from "../helpers/formatCustomDate";
import GeneralCustomTable from "../helpers/GeneralCustomTable";
import "../components/ItemT.css";

function ItemTransaction(props) {
  const transList = GetItemTransactionValue() || [];
  const setTrans = SetItemTransaction();

  // 1. States for Search and Filter
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("All");

  // 2. Extract Unique Statuses for the dropdown
  const statusOptions = useMemo(() => {
    if (!transList.length) return [];
    // Extract status names, filter out nulls, and get unique values
    const statuses = transList.map((item) => item.Order_Status_Name).filter(Boolean);
    return ["All", ...new Set(statuses)];
  }, [transList]);

  // 3. Filtering Logic (Search + Status)
  const filteredData = useMemo(() => {
    return transList.filter((item) => {
      const matchesStatus = selectedStatus === "All" || item.Order_Status_Name === selectedStatus;
      
      const searchStr = searchTerm.toLowerCase();
      const matchesSearch = 
        item.Customer_Name?.toLowerCase().includes(searchStr) ||
        item.Order_No?.toString().includes(searchStr) ||
        item.Order_Status_Name?.toLowerCase().includes(searchStr);

      return matchesStatus && matchesSearch;
    });
  }, [transList, searchTerm, selectedStatus]);

  const columns = useMemo(
    () => [
      {
        title: "Order No.",
        dataIndex: "Order_No",
        sorter: (a, b) => (a?.Order_No ?? 0) - (b?.Order_No ?? 0),
      },
      {
        title: "Date",
        dataIndex: "Order_Date",
        sorter: (a, b) => (a?.Order_Date ?? "").localeCompare(b?.Order_Date ?? ""),
        render: (record) => <span> {formatCustomDate(record)} </span>,
      },
      {
        title: "Customer",
        dataIndex: "Customer_Name",
        sorter: (a, b) => (a?.Customer_Name ?? "").localeCompare(b?.Customer_Name ?? ""),
      },
      {
        title: "Quantity",
        dataIndex: "Sales_Quantity",
        sorter: (a, b) => (a?.Sales_Quantity ?? 0) - (b?.Sales_Quantity ?? 0),
      },
      {
        title: "Amount",
        dataIndex: "Total_Amount",
        sorter: (a, b) => (a?.Total_Amount ?? 0) - (b?.Total_Amount ?? 0),
        render: (record) => <span> {formatCustomNumber(record)} </span>,
      },
      {
        title: "Status", // Changed label from Date to Status
        dataIndex: "Order_Status_Name",
        sorter: (a, b) => (a?.Order_Status_Name ?? "").localeCompare(b?.Order_Status_Name ?? ""),
      },
    ],
    []
  );

  useEffect(() => {
    itemsService.getItemTransaction(props.item_id).then((res) => {
      const serverList = res.data;
      if (serverList?.length) {
        setTrans(serverList);
      }
    });
  }, [props.item_id]);

  return (
    <div className="w-full @container" id="tabs-home" role="tabpanel">
      <div className="mt-4">
        {/* SEARCH BAR */}
        <div className="flex justify-start">
          <div className="relative w-full max-w-sm">
            <input
              className="h-[40px] flex items-center pl-10 pr-4 text-sm w-full bg-white ring-1 ring-gray-300 rounded-full outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Search by name, order no, or status"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <button className="absolute left-3 top-2.5 text-gray-500">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="size-5">
                <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
              </svg>
            </button>
          </div>
        </div>

        <div className="mt-4 space-y-4 @xs:space-y-0 @xs:flex @xs:flex-row @xs:items-center @xs:space-x-4">
          <div className="flex items-center">
            <label className="mr-2">Status:</label>
            <select
              style={{ color: "#476EF8" }}
              className="ring-[1px] ring-gray-300 rounded-md p-1"
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
            >
              {statusOptions.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* TABLE */}
      <div className="mt-7 w-full h-[500px]">
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <GeneralCustomTable
            tableHeadList={columns}
            tableDataList={filteredData} // Pass the filtered data here
            allowCheckBox={false}
            setSelectedRows={() => {}}
            rowKey={(record) => record.Order_No}
            handleRowClick={() => {}}
          />
        </div>
      </div>
    </div>
  );
}

export default ItemTransaction;